import type { ComponentProps } from "react";
import { useState } from "react";
import type { TickProps } from "@ptsd/uikit";
import {
  Card,
  ChartContainer,
  getChartLines,
  getItemIndex,
  YAxisTick,
} from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";
import { ScrollView, Text, View } from "tamagui";

import { api } from "~/utils/api";
import { EmptyState } from "~/components/blocks";

interface StepsLinearChartProps {
  researchId: string;
}

type CardProps = ComponentProps<typeof Card>;

const CHART_CONTAINER_PADDING = 32;
const WIDTH_BETWEEN_STEPS = 200;

const resoveChartWidthBySteps = (
  count: number,
  containerPadding: number,
  containerWidth: number,
  stepWidth: number,
) => {
  if (count < 4) {
    return containerWidth - containerPadding * 2;
  }
  return count * stepWidth;
};

export const StepsLinearChart: React.FC<StepsLinearChartProps & CardProps> = ({
  researchId,
  ...cardProps
}) => {
  const { t } = useTranslation("slices");
  const [chartContainerWidth, setCharContainertWidth] = useState(0);

  const [data] = api.phase.getInvolvement.useSuspenseQuery(
    {
      researchId,
    },
    {
      select: (data) => {
        const chartData = data.slices.data.map(
          ({ status: _, ...rest }) => rest,
        );

        const currentSliceDate = data.slices.data.find(
          (item) => item.status === "active",
        )?.date;

        return {
          config: {
            value: { label: "", color: "#EC1E9D" },
          },
          chartData,
          currentSliceDate,
        };
      },
    },
  );

  if (data.chartData.length === 0) {
    return (
      <EmptyState
        cardProps={{
          flex: 7,
          width: "100%",
        }}
        text={t("results.steps-linear-chart.empty-state")}
      />
    );
  }

  return (
    <Card
      flex={7}
      width="100%"
      data-id={"involvement-dynamics-chart-card"}
      paddingVertical={"$4.1"}
      shadow="md"
      gap={"$5"}
      onLayout={(e) => setCharContainertWidth(e.nativeEvent.layout.width)}
      {...cardProps}
    >
      <Card.Title textAlign="left" data-id={"involvement-dynamics-chart-title"}>
        {t("results.steps-linear-chart.title")}
      </Card.Title>

      <ScrollView
        flex={7}
        horizontal
        scrollEventThrottle={12}
        contentContainerStyle={{ flexDirection: "row" }}
      >
        <View position="relative" gap={"$4"}>
          <Text
            fontFamily={"$subtitle"}
            fontWeight={"$4"}
            fontSize={"$3"}
            lineHeight={"$2"}
            color="$gray-650"
            top={"40%"}
            left={-30}
            position="absolute"
            transform={[{ rotate: "-90deg" }]}
          >
            {t("results.steps-linear-chart.side-label")}
          </Text>
          <ChartContainer
            pointerEvents="none"
            width={resoveChartWidthBySteps(
              data?.chartData.length,
              CHART_CONTAINER_PADDING,
              chartContainerWidth,
              WIDTH_BETWEEN_STEPS,
            )}
            data-id={"involvement-dynamics-chart-container"}
            height={362}
            config={data?.config}
          >
            <LineChart
              data-id={"involvement-dynamics-chart"}
              accessibilityLayer
              data={data?.chartData}
              margin={{
                left: 10,
                right: 25,
                top: 12,
                bottom: 20,
              }}
            >
              <CartesianGrid
                data-id={"involvement-dynamics-chart-grid"}
                strokeDasharray={"3 3"}
                stroke="#CBCBCC"
                vertical={false}
              />
              <XAxis
                data-id={"involvement-dynamics-chart-x-axis"}
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={24}
                tick={{
                  fill: "#636668",
                  fontFamily: "Fixel Text",
                  fontSize: 14,
                  fontWeight: 400,
                }}
                tickFormatter={(v) => {
                  const index = getItemIndex(
                    data?.chartData,
                    "date",
                    v.toString(),
                  );
                  return "Етап " + (index + 1);
                }}
              />
              <YAxis
                data-id={"involvement-dynamics-chart-y-axis"}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                tickMargin={16}
                tick={(props: TickProps) => <YAxisTick {...props} />}
              />
              <ReferenceLine
                x={data.currentSliceDate}
                stroke="#CBCBCC"
                strokeDasharray="3 3"
              />

              {getChartLines(data?.config).map(({ dataKey, color }, i) => (
                <Line
                  data-id={`involvement-dynamics-chart-line`}
                  data-key={dataKey}
                  key={i}
                  dataKey={dataKey}
                  type="linear"
                  stroke={color}
                  strokeWidth={2.5}
                  dot={{
                    r: 8,
                    strokeWidth: 2.5,
                    stroke: color,
                    fill: "white",
                  }}
                  activeDot={{
                    r: 3,
                    strokeWidth: 2.5,
                    stroke: color,
                    fill: color,
                  }}
                />
              ))}
            </LineChart>
          </ChartContainer>
        </View>
      </ScrollView>
    </Card>
  );
};
